import { addNotification } from 'containers/Notifications/notificationsSlice'
import useTranslation from 'hooks/useTranslation'
import React, { useRef, useState } from 'react'
import { useReduxDispatch } from 'store'
import './style.scss'

interface ResendCodeProps {
  /** Tekst przycisku, np. „Przyślij ponownie” */
  text: string
  /** Domyślny odbiorca (pierwszy kanał) */
  recipient?: string
  /** Alternatywny odbiorca – jeśli jest, kliknięcie rozwija wybór kanału */
  additionalRecipient?: string
  /** Wysyłka kodu; `additional === true` oznacza wysyłkę alternatywnym kanałem */
  send: (additional: boolean) => Promise<boolean | void> | boolean | void
  /** Blokada ponownej wysyłki w ms (liczona od ostatniej wysyłki) */
  cooldown?: number
  /** Czas ostatniej wysyłki wykonanej poza komponentem (np. pierwszy kod) */
  sentAt?: number
  /** Osadzenie w wierszu flex – lista kanałów zawija się pod spodem */
  inline?: boolean
  className?: string
}

const ResendCode: React.FC<ResendCodeProps> = ({ text, recipient, additionalRecipient, send, cooldown, sentAt, inline, className }) => {
  const [isOpen, setOpen] = useState(false)
  const [isPending, setPending] = useState(false)
  const [lastAdditional, setLastAdditional] = useState(false)
  const lastSent = useRef(0)

  const dispatch = useReduxDispatch()
  const { t } = useTranslation('auth')

  const isCoolingDown = () => {
    if (!cooldown) return false
    const last = Math.max(lastSent.current, sentAt || 0)
    if (Date.now() - last > cooldown) return false
    dispatch(addNotification({ type: 'error', message: t('resendCooldown') }))
    return true
  }

  const sendCode = async (additional: boolean) => {
    setOpen(false)
    if (isPending || isCoolingDown()) return
    setPending(true)
    try {
      // `false` z funkcji wysyłki = nieudana wysyłka (bez blokady cooldown)
      if (await send(additional) === false) return
      lastSent.current = Date.now()
      setLastAdditional(additional)
    } finally {
      setPending(false)
    }
  }

  const onTriggerClick = () => {
    if (isPending) return
    if (!additionalRecipient) return sendCode(false)
    setOpen(o => !o)
  }

  const onKeyDown = (action: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
    if (e.key === 'Escape') setOpen(false)
  }

  const renderOption = (additional: boolean) => (
    <span
      role='button'
      tabIndex={isOpen ? 0 : -1}
      className={`resendCode__option${lastAdditional === additional && lastSent.current ? ' --active' : ''}`}
      onClick={() => sendCode(additional)}
      onKeyDown={onKeyDown(() => sendCode(additional))}>
      <span className='resendCode__recipient'>{additional ? additionalRecipient : recipient}</span>
      <span className='resendCode__caption'>{t(additional ? 'additionalRecipient' : 'primaryRecipient')}</span>
    </span>
  )

  return (
    <span className={`resendCode${inline ? ' --inline' : ''}${isOpen ? ' --open' : ''}${className ? ` ${className}` : ''}`}>
      <span
        role='button'
        tabIndex={0}
        aria-expanded={additionalRecipient ? isOpen : undefined}
        className={`resendCode__trigger${additionalRecipient ? ' --expandable' : ''}`}
        onClick={onTriggerClick}
        onKeyDown={onKeyDown(onTriggerClick)}>
        {isPending ? t('loading') : text}
      </span>

      {additionalRecipient && <span
        aria-hidden={!isOpen}
        className={`resendCode__collapse hidableElement --${isOpen ? 'visible' : 'hidden'}`}>
        <span>
          <span className='resendCode__options'>
            <span className='resendCode__title'>{t('chooseWhereToSendCode')}</span>
            {renderOption(false)}
            {renderOption(true)}
          </span>
        </span>
      </span>}
    </span>
  )
}

export default ResendCode
